import { RootState } from './../redux/store';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL } from '../helpers/common';
import { IPaginationResponse } from '../interfaces/responces/IPagination.response';

export type InvitationState = 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED' | 'REVOKED';
export type EntityType = 'stock_owner' | 'admin';
export type RoleType = 'stock_owner' | 'admin';
export type InviteChannel = 'email' | 'sms' | 'whatsapp';

export interface IInvitation {
  id: string;
  entityType: EntityType;
  entityId: number;
  email: string | null;
  phone: string | null;
  roleType: RoleType;
  companyId: number;
  state: InvitationState;
  approvalHash: string;
  channel: InviteChannel;
  expiresAt: string;
  approvedAt?: string;
  revokedAt?: string;
  rejectedAt?: string;
  invitedBy: number;
  metadata: {
    firstName?: string;
    lastName?: string;
    name?: string;
    language?: string;
    [key: string]: any;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ICreateInvitationRequest {
  entityType: EntityType;
  entityId: number;
  email?: string | null;
  phone?: string | null;
  roleType: RoleType;
  channel?: InviteChannel; // Optional - auto-determined by backend
  expiresAt?: string;
  metadata?: {
    firstName?: string;
    lastName?: string;
    language?: string;
    [key: string]: any;
  };
}

export interface ICreateInvitationResponse {
  invitationId: string;
  state: InvitationState;
  expiresAt: string;
  rawToken: string;
}

export interface IApproveInvitationRequest {
  token: string;
}

export interface IResendInvitationRequest {
  language?: string;
}

export interface IInvitationListParams {
  page?: number;
  limit?: number;
  entityType?: EntityType;
  entityId?: number;
  state?: InvitationState;
  roleType?: RoleType;
}

export interface IInvitationListResponse {
  invitations: IInvitation[];
  total: number;
}

export const invitationsApi = createApi({
  reducerPath: 'invitationsApi',
  tagTypes: ['Invitation', 'StockOwner'],
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: async (headers, { getState }) => {
      const token = (getState() as RootState).user.info.accessToken;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (build) => ({
    createInvitation: build.mutation<ICreateInvitationResponse, ICreateInvitationRequest>({
      transformResponse: (response: { data: ICreateInvitationResponse }) => response.data,
      query: (data) => ({
        url: '/invitations',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Invitation', 'StockOwner'],
    }),
    getInvitationById: build.query<IInvitation, string>({
      transformResponse: (response: { data: IInvitation }) => response.data,
      query: (id) => ({
        url: `/invitations/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'Invitation', id }],
    }),
    getInvitationsList: build.query<IInvitationListResponse, IInvitationListParams>({
      transformResponse: (response: { data: IInvitationListResponse }) => response.data,
      query: (params) => ({
        url: '/invitations',
        method: 'GET',
        params,
      }),
      providesTags: () => ['Invitation'],
    }),
    approveInvitation: build.mutation<IInvitation, { id: string; data: IApproveInvitationRequest }>({
      transformResponse: (response: { data: IInvitation }) => response.data,
      query: ({ id, data }) => ({
        url: `/invitations/${id}/approve`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Invitation'],
    }),
    resendInvitation: build.mutation<void, { id: string; data?: IResendInvitationRequest }>({
      query: ({ id, data }) => ({
        url: `/invitations/${id}/resend`,
        method: 'POST',
        body: data || {},
      }),
      invalidatesTags: ['Invitation'],
    }),
    revokeInvitation: build.mutation<IInvitation, string>({
      transformResponse: (response: { data: IInvitation }) => response.data,
      query: (id) => ({
        url: `/invitations/${id}/revoke`,
        method: 'POST',
      }),
      invalidatesTags: ['Invitation'],
    }),
    deleteInvitation: build.mutation<void, string>({
      query: (id) => ({
        url: `/invitations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Invitation'],
    }),
  }),
});

export const {
  useCreateInvitationMutation,
  useGetInvitationByIdQuery,
  useGetInvitationsListQuery,
  useLazyGetInvitationsListQuery,
  useApproveInvitationMutation,
  useResendInvitationMutation,
  useRevokeInvitationMutation,
  useDeleteInvitationMutation,
} = invitationsApi;

export default invitationsApi;
